import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ItemValue,
  JsonObject,
  Model,
  Question,
  QuestionRowModel,
  settings,
  surveyLocalization,
} from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey.i18n";
import { getI18n } from "react-i18next";
import "survey-core/defaultV2.min.css";

import {
  confirmActionAsyncFunc,
  findPanelIndex,
  findSectionByIndex,
  getTranslations,
  markdownHandler,
} from "./survey.util";
import { FormAction, OnboardingApplicationStatus, RoleEnum } from "../../../types";
import { initSurvey } from "./init-survey";
import { cloneDeep, get, isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import "./survey-override.css";
import { PruToast } from "../../../../../components/pru-toast";
import { fetchOnboardingFormDeriveData, mapSurveyLanguage } from "../../../network/network";
import { initFileUploadAndDownLoadAndClear } from "../file/upload-file";
import { useCanGoBack } from "../../../../../utils/hooks/use-can-go-back";
import { GlobalHelper } from "../../../../../utils/helpers/global-helper";
import { renderExternalNavigation, saveAndSubmit } from "./util.components";
import { useQuery } from "../../../../../utils/hooks/use-query";
import { isUrlInWhiteList, onboardingHttp } from "./http";
import { onboardingServerValidators } from "./server-validate";
import { backToApp, getToken, parseJwt } from "../../../utils";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../../routes/constants";
import { confirm } from "../../../../../components/modal/comfirm";
import { selectCandidateOnboardingForm } from "../../../redux/selector";
import { useSelector } from "react-redux";
import moment from "moment";

interface Props {
  json: any;
  onSubmit: (data: any, action: FormAction) => Promise<any>;
  defaultValue?: any;
  currentSectionName: string;
  setCurrentSectionName: (section: string) => void;
  disabled?: boolean;
  npaNumber?: string;
}

const normalizeValue = (value: string): string => {
  return value.trimStart().replace(/\s{2,}/g, " ");
};

export const SurveyComponent: React.FC<Props> = ({
  onSubmit,
  json,
  defaultValue,
  currentSectionName,
  setCurrentSectionName,
  npaNumber,
}) => {
  initSurvey();
  const [defaultFormVal, setDefaultFormVal] = useState(defaultValue);
  const candidateOnboardingForm = useSelector(selectCandidateOnboardingForm);
  useEffect(() => {
    setDefaultFormVal((pre: any) => {
      if (isEqual(pre, defaultValue)) {
        return pre;
      } else {
        return defaultValue;
      }
    });
  }, [defaultValue]);
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const query = useQuery();
  const [survey, setSurvey] = useState(new Model(json));
  const [pageNo, setPageNo] = React.useState(-1);
  const dataRef = useRef<any>({});
  const isLastSectionSubmitted = useRef<boolean>(false);
  const { isLastSection, isFirstSection } = useMemo(() => {
    const currentModule = json.pages.find((module: any) =>
      module.elements.find((section: any) => section.name === currentSectionName),
    );
    if (!currentModule) {
      return {};
    }
    const result: any = {
      isLastSection: cloneDeep(currentModule.elements).pop().name === currentSectionName,
      isFirstSection: currentModule.elements[0].name === currentSectionName,
    };
    return result;
  }, [json, currentSectionName]);
  useEffect(() => {
    if (currentSectionName) {
      const pageNo = findPanelIndex(json, currentSectionName);
      setPageNo(pageNo);
    }
  }, [json, currentSectionName]);
  useEffect(() => {
    if (!survey.currentPage) return;
    const currentPageQuestions = survey.currentPage.questions;
    const currentPageQuestionsValue = {} as any;
    currentPageQuestions.forEach((question: any) => {
      currentPageQuestionsValue[question.name] = survey.data[question.name];
    });
    dataRef.current.questions = currentPageQuestionsValue;
    dataRef.current.originQuestions = currentPageQuestionsValue;
  }, [survey, currentSectionName]);

  const saveForm = useCallback(
    async (
      data: {
        originQuestions: any;
        questions: any;
      },
      action = FormAction.VALIDATION,
    ) => {
      const { originQuestions, questions } = data;
      if (action !== FormAction.SUBMIT && isEqual(originQuestions, questions)) {
        return;
      }
      const response: any = await onSubmit(questions, action);
      const errors = response?.error ?? response?.payload?.errors;
      if (response?.backToFak) {
        const message = getTranslations().the_npa_hase_been_cancelled;
        PruToast({
          message: message,
        });
      } else if (!errors && !response?.noNeedToast) {
        const message =
          action === FormAction.SAVE
            ? getTranslations().the_data_has_been_saved
            : getTranslations().the_data_has_been_submitted;
        PruToast({
          message: message,
        });
        if (
          currentSectionName == "identificationInformation" &&
          !npaNumber &&
          action == FormAction.SUBMIT
        ) {
          navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/form/create-npa?${query.toString()}`);
        }
      }
      return response;
    },
    [onSubmit],
  );
  useEffect(() => {
    if (pageNo > survey.pageCount - 1) {
      if (canGoBack()) navigate(-1);
    }
    const updatedCurrentSection = findSectionByIndex(json, pageNo);
    if (updatedCurrentSection) {
      GlobalHelper.setOnboardingCurrentSection({
        ...GlobalHelper.getOnboardingCurrentSection(),
        ...updatedCurrentSection,
      });
      setCurrentSectionName(updatedCurrentSection.name);
    }
  }, [json, pageNo, saveForm, setCurrentSectionName, query]);
  useEffect(() => {
    isLastSectionSubmitted.current = false;
    return () => {
      const handleSave = async () => {
        const response = await saveForm(dataRef.current, FormAction.SAVE);
        const errors = response?.error ?? response?.payload?.errors;
        if (errors) {
          PruToast({
            message: "Failed to save",
          });
        }
      };
      if (!isLastSectionSubmitted.current) {
        handleSave();
      }
    };
  }, []);
  const decrementPageNo = React.useCallback(async () => {
    const response = await saveForm(dataRef.current, FormAction.SAVE);
    const errors = response?.error ?? response?.payload?.errors;
    if (!errors) {
      setPageNo(pageNo - 1);
    }
  }, [saveForm, pageNo]);

  const incrementPageNo = React.useCallback(async () => {
    await saveAndSubmit({
      survey,
      pageNo,
      isLastSection,
      canGoBack,
      navigate,
      setPageNo,
      saveForm,
      dataRef,
      isLastSectionSubmitted,
      query,
    });
  }, [survey, pageNo, isLastSection, canGoBack, navigate, saveForm, query]);
  const actionButtonsUI = useMemo(() => {
    const rawToken = getToken();
    const tokenData = rawToken ? parseJwt(rawToken) : {};
    if (tokenData.role === RoleEnum.MANAGER && currentSectionName == "eSignatureSection") {
      return false;
    } else {
      return renderExternalNavigation({
        survey,
        currentSectionName,
        isFirstSection,
        decrementPageNo,
        incrementPageNo,
      });
    }
  }, [survey, isFirstSection, currentSectionName, decrementPageNo, incrementPageNo]);
  useEffect(() => {
    const _survey = new Model({ ...json });
    setSurvey(_survey);
  }, [json]);
  const surveyFormUI = useMemo(() => {
    const appLang = getI18n().language;
    const surveyLang = mapSurveyLanguage(appLang);
    const questionZipCodeList = ["kodePosKTP", "kodePosDomisili", "kodePosKorespondensi"];
    const zipCodeIdList = [
      "provinsiKTP",
      "kotaKabupatenKTP",
      "kecamatanKTP",
      "kelurahanKTP",
      "kodePosKTP",
    ];
    const zipCodeDomicileList = [
      "provinsiDomisili",
      "kotaKabupatenDomisili",
      "kecamatanDomisili",
      "kelurahanDomisili",
      "kodePosDomisili",
    ];
    const zipCodeCorrespondenceList = [
      "provinsiKorespondensi",
      "kotaKabupatenKorespondensi",
      "kecamatanKorespondensi",
      "kelurahanKorespondensi",
      "kodePosKorespondensi",
    ];
    const dataCandidate = candidateOnboardingForm.modules;
    onboardingServerValidators.setEnv({ lang: appLang });
    survey.locale = surveyLang;
    settings.localization.defaultLocaleName = surveyLang;
    settings.dropdownSearchDelay = 1000;
    surveyLocalization.defaultLocale = surveyLang;
    survey.showPageTitles = false;
    survey.showNavigationButtons = "none";
    survey.questionErrorLocation = "bottom";
    survey.showCompletedPage = false;
    survey.mergeData(defaultFormVal);
    survey.onComplete.add((_sender: any) => {});
    survey.onValueChanged.add((_sender, options) => {
      if (typeof options.value === "string") {
        const normalizedText = normalizeValue(options.value);
        if (normalizedText !== options.value) {
          options.value = normalizedText;
          options.question.value = normalizedText;
        }
      }

      function setAndResetQuestionZipCode(
        questionName: string,
        dataAddress: string,
        typeTask: string,
        currentData: any,
      ) {
        let listAddress =
          questionName == "kodePosKTP"
            ? zipCodeIdList
            : questionName == "kodePosDomisili"
            ? zipCodeDomicileList
            : questionName == "kodePosKorespondensi"
            ? zipCodeCorrespondenceList
            : [];
        if (typeTask == "SET") {
          let parseZipCodeKTP = dataAddress.split(" - ");
          survey.getQuestionByName(listAddress[0]).value = parseZipCodeKTP[4];
          survey.getQuestionByName(listAddress[1]).value = parseZipCodeKTP[3];
          survey.getQuestionByName(listAddress[2]).value = parseZipCodeKTP[2];
          survey.getQuestionByName(listAddress[3]).value = parseZipCodeKTP[1];
          if (currentData.selectAlamatDom == true) {
            let getFieldAddressDom = zipCodeDomicileList;
            survey.getQuestionByName(getFieldAddressDom[0]).value = parseZipCodeKTP[4];
            survey.getQuestionByName(getFieldAddressDom[1]).value = parseZipCodeKTP[3];
            survey.getQuestionByName(getFieldAddressDom[2]).value = parseZipCodeKTP[2];
            survey.getQuestionByName(getFieldAddressDom[3]).value = parseZipCodeKTP[1];
            survey.getQuestionByName(getFieldAddressDom[4]).value = dataAddress;
          }
          const docileBlackList = [
            "NIAS",
            "NIAS SELATAN",
            "NIAS UTARA",
            "NIAS BARAT",
            "GUNUNGSITOLI",
          ].includes(`${parseZipCodeKTP[3]}`.toUpperCase());
          if (docileBlackList) {
            confirm({
              message: getI18n().t("onboarding.disclaimer_domicile"),
              confirmButtonText: "OK",
              disableCancel: true,
              onConfirm: () => {
                backToApp();
              },
            });
          }

          return true;
        } else if (typeTask == "RESET") {
          survey.getQuestionByName(listAddress[0]).value = "";
          survey.getQuestionByName(listAddress[1]).value = "";
          survey.getQuestionByName(listAddress[2]).value = "";
          survey.getQuestionByName(listAddress[3]).value = "";
        }
      }
      dataRef.current.questions = { ...dataRef.current.questions, [options.name]: options.value };
      if (questionZipCodeList.indexOf(options.name) > -1) {
        if (options.value != undefined) {
          setAndResetQuestionZipCode(options.name, options.value, "SET", dataRef.current.questions);
        } else {
          setAndResetQuestionZipCode(
            options.name,
            options.value,
            "RESET",
            dataRef.current.questions,
          );
        }
      }
      if (options.name == "selectAlamatDom" && options.value == true) {
        let getFieldAddressID = zipCodeIdList;
        let getFieldAddressDom = zipCodeDomicileList;
        let dataCurrent = dataRef.current.questions;
        survey.getQuestionByName(getFieldAddressDom[0]).value = dataCurrent[getFieldAddressID[0]];
        survey.getQuestionByName(getFieldAddressDom[1]).value = dataCurrent[getFieldAddressID[1]];
        survey.getQuestionByName(getFieldAddressDom[2]).value = dataCurrent[getFieldAddressID[2]];
        survey.getQuestionByName(getFieldAddressDom[3]).value = dataCurrent[getFieldAddressID[3]];
        survey.getQuestionByName(getFieldAddressDom[4]).value = dataCurrent[getFieldAddressID[4]];
        survey.getQuestionByName("jalanDomisili").value = dataCurrent["jalanKTP"];
        survey.getQuestionByName("blokNoDomisili").value = dataCurrent["blokNoKTP"];
        survey.getQuestionByName("rtDomisili").value = dataCurrent["rtKTP"];
        survey.getQuestionByName("rwDomisili").value = dataCurrent["rwKTP"];
      }
      if (options.name == "jalanKTP" && dataRef.current.questions.selectAlamatDom == true) {
        survey.getQuestionByName("jalanDomisili").value = options.value;
      }
      if (options.name == "blokNoKTP" && dataRef.current.questions.selectAlamatDom == true) {
        survey.getQuestionByName("blokNoDomisili").value = options.value;
      }
      if (options.name == "rtKTP" && dataRef.current.questions.selectAlamatDom == true) {
        survey.getQuestionByName("rtDomisili").value = options.value;
      }
      if (options.name == "rwKTP" && dataRef.current.questions.selectAlamatDom == true) {
        survey.getQuestionByName("rwDomisili").value = options.value;
      }
      if (options.question.name == "taxStatus" && options.value == "BAPAK") {
        if (
          moment().diff(
            moment(
              candidateOnboardingForm.modules.personalData.identificationInformation.questionAnswers
                .dateOfBirth,
            ),
            "years",
            true,
          ) >= 18
        ) {
          confirm({
            message: getI18n().t("onboarding.tax_status_bapak"),
            confirmButtonText: "OK",
            disableCancel: true,
          });
        }
      } else if (
        options.question.name == "city" &&
        ["NIAS", "NIAS SELATAN", "NIAS UTARA", "NIAS BARAT", "GUNUNGSITOLI"].includes(
          `${options.value}`.toUpperCase(),
        )
      ) {
        dataRef.current.questions = { ...dataRef.current.questions, [options.name]: "" };
        confirm({
          message: getI18n().t("onboarding.disclaimer_domicile"),
          confirmButtonText: "OK",
          disableCancel: true,
          onConfirm: () => {
            backToApp();
          },
        });
      }
    });
    survey.onTextMarkdown.add(markdownHandler);
    survey.onChoicesLazyLoad.add(async (_, options) => {
      const choicesLazyLoadByUrl = options.question?.choicesLazyLoadByUrl;
      if (choicesLazyLoadByUrl?.url) {
        let url = "";
        if (questionZipCodeList.indexOf(options.question.name) > -1) {
          url = `${choicesLazyLoadByUrl.url}?zipCode=${options.filter}`;
        } else {
          url = `${choicesLazyLoadByUrl.url}${
            choicesLazyLoadByUrl.url.includes("?") ? "&" : "?"
          }skip=${options.skip}&take=${options.take}&filter=${options.filter}`;
        }
        await onboardingHttp.get(url).then(({ data }) => {
          let _options: any[];
          if (choicesLazyLoadByUrl?.path) {
            _options = get(data, choicesLazyLoadByUrl?.path);
          } else {
            _options = data;
          }
          let opts: any;

          if (options.question.name == "agencyMarketingOffice") {
            opts = _options.map((option) => {
              return {
                value: option.aobItem,
                text: `${option.aobItem} - ${option.aobDescriptionInd}`,
              };
            });
          } else if (questionZipCodeList.indexOf(options.question.name) > -1) {
            opts = _options.map((option) => {
              return {
                value: `${option.zipCode} - ${option.kelurahan} - ${option.kecamatan} - ${option.kabupaten} - ${option.province} - ${option.provinceCode}`,
                text: `${option.zipCode} - ${option.kelurahan} - ${option.kecamatan} - ${option.kabupaten} - ${option.province} - ${option.provinceCode}`,
                customProperty: option,
              };
            });
          } else {
            opts = _options.map((option) => {
              return {
                value: get(option, choicesLazyLoadByUrl.valueName, option.value),
                text: get(option, choicesLazyLoadByUrl.titleName, option.text),
                customProperty: option,
              };
            });
          }
          options.setItems(opts, data.total);
        });
      }
    });
    let surveyDisplayLabelProceed: any = {};
    survey.onGetChoiceDisplayValue.add(async (_, options) => {
      const choicesLazyLoadByUrl = options.question?.choicesLazyLoadByUrl;
      const filterStr = options.values
        .map((value) => `filters[${choicesLazyLoadByUrl.valueName}]=` + value)
        .join("&");
      const questionName = options.question.name;
      if (!surveyDisplayLabelProceed[questionName] && choicesLazyLoadByUrl?.url) {
        let url = "";
        if (questionZipCodeList.indexOf(options.question.name) > -1) {
          url = `${choicesLazyLoadByUrl.url}?zipCode=${options.values[0].split(" - ")[0]}`;
        } else {
          url = `${choicesLazyLoadByUrl.url}${
            choicesLazyLoadByUrl.url.includes("?") ? "&" : "?"
          }skip=0&take=100&${filterStr}`;
        }
        await onboardingHttp.get(url, {}).then(({ data }) => {
          let _options: any[];
          if (choicesLazyLoadByUrl?.path) {
            _options = get(data, choicesLazyLoadByUrl?.path);
          } else {
            _options = data;
          }
          const labels = _options
            .map((option, index) => {
              const optValue = get(option, choicesLazyLoadByUrl.valueName, option.value);
              if (questionZipCodeList.indexOf(options.question.name) > -1) {
                if (options.values.length > 0) {
                  let dataSplit = options.values[0].split(" - ");
                  var _index: any = dataSplit[0].indexOf(optValue);
                }
              } else {
                var _index: any = options.values.indexOf(optValue);
              }
              if (_index !== -1) {
                options.values.splice(index, 1);
                if (options.question.name == "agencyMarketingOffice") {
                  return `${option.aobItem} - ${option.aobDescriptionInd}`;
                } else if (questionZipCodeList.indexOf(options.question.name) > -1) {
                  // const kelurahan = dataCandidate.personalData.contactInformation.questionAnswers[options.question.name].split(' - ');
                  const getZipCode = options.values;
                  if (getZipCode[1] == option.kelurahan) {
                    return `${option.zipCode} - ${option.kelurahan}`;
                  }
                } else {
                  return get(option, choicesLazyLoadByUrl.titleName, option.text);
                }
              }
            })
            .filter((item) => item !== undefined);
          options.setItems(labels.concat(options.values));
          surveyDisplayLabelProceed[questionName] = true;
        });
      }
    });
    survey.onAfterRenderQuestion.add((_sender, options) => {
      if (options.question.triggerPopulate?.type === "onInit") {
        const triggerPopulate = options.question.triggerPopulate;
        fetchOnboardingFormDeriveData({
          ...triggerPopulate,
        }).then((response) => {
          const populatedData = response?.data;
          if (populatedData) {
            for (const key in populatedData) {
              const question = survey.getQuestionByName(key);
              question.value = populatedData[key];
            }
          }
        });
      }
      const listQuestionOpenReadOnly = ["firstNameLocal", "identityDocumentNumber", "sex"];
      if (
        listQuestionOpenReadOnly.indexOf(options.question.name) != -1 &&
        candidateOnboardingForm.applicationStatus == OnboardingApplicationStatus.FOLLOW_UP_REQUIRED
      ) {
        options.question.readOnly = false;
      }
    });

    /** Use this event to specify a custom error message */
    survey.onValidateQuestion.add((_, options) => {
      const value = options.value;

      const questionType = options.question.getType();
      if (questionType === "file") {
        // validate minimum number of upload-files
        const minFileNumber = options.question.minFileNumber;

        if (minFileNumber && Array.isArray(value) && value.length < minFileNumber) {
          options.error = getI18n().t("onboarding.minimum_file_number", {
            min: minFileNumber,
          });
          return;
        }
      }
      // other validation
      // ...
    });

    settings.web.onBeforeRequestChoices = (sender: any, options: any) => {
      const question = sender.owner.jsonObj;
      const choicesByUrl: any = question?.choicesByUrl;
      const APIM_BASE_URL = get(window, "envConfig.REACT_APP_APIM_BASE_URL", "");
      const isApimUrl = choicesByUrl?.url && choicesByUrl?.url.startsWith(APIM_BASE_URL);
      if (isApimUrl) {
        options.request.setRequestHeader(
          "Ocp-Apim-Subscription-Key",
          get(window, "envConfig.REACT_APP_OEM_APIM_SUBSCRIPTION_KEY", ""),
        );
        if (isUrlInWhiteList(choicesByUrl?.url)) {
          options.request.setRequestHeader("Authorization", getToken());
        } else {
          options.request.setRequestHeader("Authorization", undefined);
        }
      } else {
        options.request.setRequestHeader("Ocp-Apim-Subscription-Key", undefined);
        options.request.setRequestHeader("Authorization", undefined);
      }
    };
    settings.confirmActionAsync = confirmActionAsyncFunc;
    initFileUploadAndDownLoadAndClear(survey);
    return pageNo > -1 ? (
      <div className="flex flex-1 flex-col">
        <div className="flex flex-1 flex-col w-screen pb-4">
          <Survey
            currentPageNo={pageNo}
            className="survey-common"
            id="onboarding-survey"
            model={survey}
          />
        </div>
        {actionButtonsUI}
      </div>
    ) : null;
  }, [survey, defaultFormVal, pageNo, actionButtonsUI]);
  return surveyFormUI;
};
